import React, { useRef } from 'react';
import PropTypes from 'prop-types';

import ExclamationCircleF from '@decisiv/iconix/lib/components/ExclamationCircleF';
import QuestionCircleF from '@decisiv/iconix/lib/components/QuestionCircleF';
import Table from '@decisiv/iconix/lib/components/Table';
import Trash from '@decisiv/iconix/lib/components/Trash';
import Upload from '@decisiv/iconix/lib/components/Upload';

import color from '@decisiv/design-tokens/lib/color';
import toColorString from 'polished/lib/color/toColorString';

import Button from '@decisiv/ui-components/lib/components/Button';
import RequiredIcon from '@decisiv/ui-components/lib/atoms/RequiredIcon';
import { H3, H4, P } from '@decisiv/ui-components/lib/components/Typography';

import allowDefaultActionInModal from '../../../shared/modalClick';
import { formatFileSize } from './fileValidation';
import { REQUIRED_FIELDS, UPLOAD_ERRORS, UPLOAD_STEP } from './copy';
import { TEMPLATE_PATH } from '../../services/bulkUserImportService';
import {
  FieldGroup,
  FieldHelp,
  FileRow,
  HiddenFileInput,
  HintRow,
  RequiredMarker,
  StepBody,
  StepHeading,
  TemplateLink,
  UploadActions,
} from './styles';

const WARNING = toColorString(color.status.warning.medium);
// The "?" is drawn lighter than the sentence beside it — Figma names the fill
// "VI/Licorice Mousse - Medium" (#9C9FAB), where the text is Alaskan Husky.
const HINT = toColorString(color.visualInterest.licoriceMousse.medium);

/**
 * Step 1: choose a CSV, or fetch the template to fill in (Figma 787:76931).
 *
 * The file picker is a hidden native input fronted by a KeyDS Button, which is
 * the only way to get the library's control to open one — `<input type="file">`
 * cannot be styled into the mockup's button, and Button cannot open a picker.
 *
 * The template is an ordinary link rather than a fetch: it is a static file, and
 * letting the browser download it avoids holding a CSV in memory to no purpose.
 *
 * Both hint lines carry an icon, as all four upload frames draw them: a grey "?"
 * beside the limits, and an amber "!" beside a rejection. The message itself
 * stays in ordinary body colour — 1.2.1/1.2.2/1.2.3 put the colour in the icon
 * and the border, not in the text.
 */
function UploadStep({ error, file, isUploading, onChooseFile, onRemoveFile }) {
  const inputRef = useRef(null);

  const handleChange = (event) => {
    const [chosen] = event.target.files || [];
    if (chosen) onChooseFile(chosen);

    // Cleared so that choosing the same file twice still fires a change event,
    // which is what lets an admin retry after fixing the file on disk. Read off
    // the ref rather than the event, whose target is a function parameter.
    if (inputRef.current) inputRef.current.value = '';
  };

  // Only 1.2.1, the over-size frame, tints the file's size; the type and content
  // errors leave it grey. The size is what that error is about.
  const sizeIsAtFault = error === UPLOAD_ERRORS.tooLarge;

  return (
    <StepBody>
      <StepHeading>
        <H3 style={{ margin: 0 }}>{UPLOAD_STEP.title}</H3>
        <RequiredMarker>
          <RequiredIcon />
          <P shade={1} size="small" style={{ margin: 0 }}>
            {REQUIRED_FIELDS}
          </P>
        </RequiredMarker>
      </StepHeading>

      <FieldGroup>
        {/* Figma gives this "Header 4/Medium" — Montserrat Medium 16 — which is
            H4, not a bolded paragraph. */}
        <H4 style={{ margin: 0 }}>{UPLOAD_STEP.fieldLabel}</H4>
        <FieldHelp>
          <P shade={1} size="small" style={{ margin: 0 }}>
            {UPLOAD_STEP.help}
          </P>
        </FieldHelp>

        {file ? (
          <FileRow $invalid={Boolean(error)}>
            <div>
              <P style={{ margin: 0 }}>{file.name}</P>
              <P
                shade={sizeIsAtFault ? undefined : 1}
                size="small"
                style={{
                  margin: 0,
                  color: sizeIsAtFault ? WARNING : undefined,
                }}
              >
                {formatFileSize(file.size)}
              </P>
            </div>
            <Button
              aria-label={UPLOAD_STEP.remove}
              disabled={isUploading}
              icon={Trash}
              kind="secondary"
              size="small"
              onClick={onRemoveFile}
            />
          </FileRow>
        ) : (
          <UploadActions>
            <Button
              icon={Upload}
              kind="secondary"
              size="small"
              text={UPLOAD_STEP.upload}
              onClick={() => inputRef.current?.click()}
            />
            <TemplateLink
              download
              icon={Table}
              text={UPLOAD_STEP.template}
              to={TEMPLATE_PATH}
              onClick={allowDefaultActionInModal}
            />
          </UploadActions>
        )}

        {error ? (
          <HintRow>
            <ExclamationCircleF color={WARNING} size="small" />
            <P size="small" style={{ margin: 0 }}>
              {error}
            </P>
          </HintRow>
        ) : (
          <HintRow>
            <QuestionCircleF color={HINT} size="small" />
            <P shade={1} size="small" style={{ margin: 0 }}>
              {UPLOAD_STEP.limits}
            </P>
          </HintRow>
        )}
      </FieldGroup>

      <HiddenFileInput
        accept=".csv,text/csv"
        data-testid="bulk-user-import-file-input"
        ref={inputRef}
        type="file"
        onChange={handleChange}
        onClick={allowDefaultActionInModal}
      />
    </StepBody>
  );
}

UploadStep.propTypes = {
  /** Why the chosen file was rejected, from this side or the server's. */
  error: PropTypes.string,
  file: PropTypes.shape({ name: PropTypes.string, size: PropTypes.number }),
  /**
   * Upload in flight. The mockup puts a "PROCESSING..." state on the upload
   * button itself (1.0 | Processing), but by the time an upload can start a file
   * has been chosen, and that button has been replaced by the file row. So the
   * progress shows on CONTINUE — see index.jsx — and all this does here is stop
   * the admin pulling the file out from under a request already sent.
   */
  isUploading: PropTypes.bool,
  onChooseFile: PropTypes.func.isRequired,
  onRemoveFile: PropTypes.func.isRequired,
};

UploadStep.defaultProps = {
  error: null,
  file: null,
  isUploading: false,
};

export default UploadStep;
